import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import {
  FaMicrochip,
  FaRobot,
  FaChartBar,
  FaWifi,
  FaBolt,
  FaCog,
  FaChartLine,
  FaShieldAlt,
  FaMobile,
  FaCloud,
  FaTools,
  FaRocket,
  FaTachometerAlt,
  FaPlug,
  FaDesktop,
  FaSlidersH,
  FaArrowRight,
  FaArrowLeft,
  FaIndustry,
  FaCheckCircle,
  FaLightbulb,
  FaTimes
} from 'react-icons/fa';
import './Projects.css';

const Projects = () => {
  const [activeGallery, setActiveGallery] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Helper to load images dynamically
  function importAll(r) {
    return r.keys().sort().map(r);
  }

  // Load gallery images dynamically
  const pcbImages = importAll(require.context('../assets/gallery/pcb-jig', false, /\.(png|jpe?g|svg)$/));
  const roboticImages = importAll(require.context('../assets/gallery/robotic-cell', false, /\.(png|jpe?g|svg)$/));
  const monitoringImages = importAll(require.context('../assets/gallery/monitoring-suite', false, /\.(png|jpe?g|svg)$/));

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.fade-in, .fade-up, .scale-up, .slide-in-left, .slide-in-right, .slide-in-blur-left, .slide-in-blur-right');
    elements.forEach(el => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const openGallery = (productIndex) => {
    setActiveGallery(productIndex);
    setCurrentImageIndex(0);
  };

  const closeGallery = () => {
    setActiveGallery(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (activeGallery !== null) {
      const images = featuredProducts[activeGallery].images;
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (activeGallery !== null) {
      const images = featuredProducts[activeGallery].images;
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const featuredProducts = [
    {
      badge: "Flagship",
      title: "PCB Test Jig Platform (ICT & FCT)",
      seoTagline: "High-volume PCB test jigs for electronics manufacturing, ICT and FCT automation.",
      description: "Modular ICT and FCT PCB test jig platform for electronics manufacturing companies that are tired of manual probing, inconsistent test results and last-minute rework. Designed for high-volume production lines where reliable, repeatable testing is non-negotiable.",
      features: [
        "Automated ICT and FCT testing for PCBs",
        "Pneumatic and mechanical clamping for repeatable contact",
        "Supports multiple PCB variants with quick-change fixtures",
        "Seamless integration with existing test equipment and software",
        "Automatic test result logging, reports and traceability"
      ],
      idealFor: "Electronics and PCB manufacturers looking to automate in-circuit test (ICT), functional test (FCT) and end-of-line testing.",
      exampleProject: "Designed an ICT/FCT test jig platform for a motor controller PCB, reducing manual test time by 60% and cutting field failures by more than half.",
      images: pcbImages,
      mainImage: pcbImages[0],
      altText: "PCB ICT FCT test jig platform for electronics manufacturing",
      price: "Custom-engineered for your product",
      primaryBtn: "Request Demo",
      fallbackIcon: "microchip"
    },
    {
      badge: "Most Adopted",
      title: "Robotic & SPM Cells",
      seoTagline: "Robotic welding automation, special purpose machines, assembly automation cells.",
      description: "Pre-engineered robotic and special purpose machine (SPM) cells for welding, assembly, material handling and inspection. Built for factories that want stable quality, faster cycle time and less dependence on highly skilled manual operators.",
      features: [
        "Robotic cells with Delta, Yaskawa, Fanuc and other industrial robots",
        "Custom grippers, fixtures and tooling for your parts",
        "Support for welding, assembly, pick & place and inspection operations",
        "Machine vision integration for position detection and defect checking",
        "Safety-compliant layouts with guards, interlocks and emergency stops"
      ],
      idealFor: "Automotive component manufacturers, fabrication shops and OEMs looking to move from manual operations to robotic and SPM-based automation.",
      exampleProject: "Robotic welding cell for an automotive bracket, replacing two manual stations and increasing output by 40% with consistent weld quality.",
      images: roboticImages,
      mainImage: roboticImages[0],
      altText: "Robotic welding and assembly automation cell with industrial robot",
      price: "Designed around your process",
      primaryBtn: "Talk to an Expert",
      fallbackIcon: "robot"
    },
    {
      badge: "New",
      title: "Monitoring & Analytics Suite",
      seoTagline: "Industrial IoT platform, OEE monitoring system, factory production dashboard.",
      description: "An industrial monitoring and analytics suite that connects your machines, PLCs, test jigs and sensors into one dashboard. Designed for factories that want real-time production visibility, OEE monitoring and data-driven decision-making.",
      features: [
        "Live production, OEE and downtime dashboards",
        "Data acquisition from PLCs, test jigs, robots and sensors",
        "Alerts and notifications for breakdowns, quality issues and delays",
        "Cloud or on-premise deployment options",
        "Access from web, desktop and mobile devices"
      ],
      idealFor: "Plant heads, production managers and maintenance teams who need real-time KPIs instead of manual Excel reports.",
      exampleProject: "Implemented OEE and downtime monitoring for a multi-line fabrication plant, revealing hidden losses and helping increase line efficiency by 18%.",
      images: monitoringImages,
      mainImage: monitoringImages[0],
      altText: "Industrial production monitoring and OEE analytics dashboard",
      price: "Flexible licensing for every plant",
      primaryBtn: "Request Live Demo",
      fallbackIcon: "chart"
    }
  ];

  const hardwareDevices = [
    {
      icon: <FaCog />,
      title: "PLC & Control Panels",
      description: "Custom PLC and control panels designed for industrial automation systems—clear wiring, labelled terminals and documentation that makes maintenance easier for your team.",
      keywords: "PLC control panel manufacturer, industrial control panel design, automation control panel",
      exampleProject: "PLC control panel for a multi-axis SPM with servo drives, safety interlocks and HMI interface.",
      price: "Built to your specification"
    },
    {
      icon: <FaBolt />,
      title: "Motor Control & Drive Panels",
      description: "MCC, DOL, star–delta and VFD panels engineered for reliable motor control in heavy-duty industrial environments, with proper protections and interlocks.",
      keywords: "Motor control centre, VFD panel, DOL starter panel, industrial motor control",
      exampleProject: "VFD and soft-starter panel for a pump system, reducing starting current and improving energy efficiency.",
      price: "Project-based pricing"
    },
    {
      icon: <FaWifi />,
      title: "IIoT Edge & Gateways",
      description: "Industrial IoT gateways that connect legacy machines, PLCs and field devices to SCADA, cloud or analytics systems using protocols like Modbus, MQTT and OPC UA.",
      keywords: "IIoT gateway, industrial IoT device, Modbus to MQTT, OPC UA integration",
      exampleProject: "IIoT gateway deployed on existing PLC lines to stream data to a cloud OEE dashboard without changing the control logic.",
      price: "Scalable per installation"
    },
    {
      icon: <FaDesktop />,
      title: "HMI & Operator Stations",
      description: "Operator stations with HMIs, push buttons and indicators designed for everyday use on the shop floor—simple for operators, powerful for engineers.",
      keywords: "HMI panel, operator console, industrial HMI system",
      exampleProject: "Multi-screen HMI station for an assembly line, providing step-by-step operator guidance and live status.",
      price: "Configured for each line"
    },
    {
      icon: <FaTachometerAlt />,
      title: "Energy & Utility Monitoring Kits",
      description: "Metering solutions for energy, compressed air, water and other utilities to uncover leaks, wastage and hidden operating costs.",
      keywords: "Energy monitoring system, industrial energy management, utility monitoring",
      exampleProject: "Energy monitoring for a welding shop, identifying high-consumption areas and reducing monthly power costs.",
      price: "Custom monitoring packages"
    },
    {
      icon: <FaSlidersH />,
      title: "Sensor & Instrumentation Packs",
      description: "Pre-selected packages of industrial sensors and instrumentation for common automation applications, reducing engineering and selection time.",
      keywords: "Industrial sensors, process instrumentation package, automation sensor kit",
      exampleProject: "Standard sensor pack for a material handling line including proximity, photoelectric and encoder sensors.",
      price: "Application-based bundles"
    }
  ];

  const testingEquipment = [
    {
      icon: <FaTools />,
      title: "Custom PCB & Product Fixtures",
      description: "Custom fixtures designed for PCB, module and product testing—whether you need ICT, FCT or functional test at the end of the line.",
      keywords: "Custom PCB test fixture, ICT FCT fixture, functional test jig",
      exampleProject: "Combined ICT/FCT fixture for a power electronics PCB with multiple test points and high-current contacts.",
      price: "Designed per product family"
    },
    {
      icon: <FaCog />,
      title: "Automated Test Stations",
      description: "Semi-automatic and fully automatic test stations with programmable sequences, barcodes and automatic pass/fail logging for traceable quality control.",
      keywords: "Automated test system, end-of-line tester, automated functional test station",
      exampleProject: "End-of-line automated test station for a control panel, reducing test time from 20 minutes to under 7 minutes.",
      price: "Turnkey project pricing"
    },
    {
      icon: <FaRocket />,
      title: "Performance & Endurance Testers",
      description: "Application-specific rigs for performance, endurance and life testing under realistic load and environmental conditions.",
      keywords: "Endurance test rig, life cycle tester, performance test bench",
      exampleProject: "Endurance test bench for pump controllers running 24x7 under varying load profiles.",
      price: "Configured to your test profile"
    },
    {
      icon: <FaShieldAlt />,
      title: "Electrical Safety Test Systems",
      description: "Safety test setups for hipot, insulation resistance, ground bond and leakage current to help you comply with electrical safety standards.",
      keywords: "Hipot test system, electrical safety tester, insulation resistance testing",
      exampleProject: "Safety test setup for a panel OEM including hipot and ground-bond tests for every outgoing unit.",
      price: "Compliance-focused solutions"
    },
    {
      icon: <FaChartLine />,
      title: "Test Data Loggers",
      description: "Multi-channel data loggers capturing temperature, voltage, current, vibration and more, with exportable reports for validation and audits.",
      keywords: "Industrial data logger, test data acquisition, temperature and voltage logger",
      exampleProject: "Data logger solution for capturing long-duration performance data from prototype inverters.",
      price: "Channel-based configurations"
    },
    {
      icon: <FaPlug />,
      title: "Calibration & Validation Tools",
      description: "Calibration tools and procedures to keep your test setups and sensors within tolerance and ready for internal or external audits.",
      keywords: "Calibration equipment, instrument calibration service, test bench calibration",
      exampleProject: "Periodic calibration setup for ICT/FCT stations to maintain consistency across multiple lines.",
      price: "Service & equipment options"
    }
  ];

  const softwareSuites = [
    {
      icon: <FaChartLine />,
      title: "Production & OEE Analytics",
      description: "Analytics platform for tracking OEE, downtime, quality losses and production efficiency across machines and lines. Designed for factories that are stuck in Excel hell and want real-time, accurate numbers.",
      features: [
        "Real-time OEE and KPIs",
        "Downtime and loss categorization",
        "Custom dashboards for roles",
        "API integration with MES/ERP"
      ],
      exampleProject: "OEE and downtime analytics deployed in a fabrication shop, helping identify the top 3 loss reasons and recover several hours per shift.",
      price: "Licensing options available"
    },
    {
      icon: <FaMobile />,
      title: "Shop-Floor Monitoring App",
      description: "Mobile app that gives supervisors and managers live visibility of line status, key KPIs and alarms on their phones—on the shop floor or off-site.",
      features: [
        "Live line and machine status",
        "Push alerts for critical events",
        "Works online and on local network",
        "Android and iOS support"
      ],
      exampleProject: "Monitoring app for plant managers that shows real-time status for each line, with alerts for breakdown and quality issues.",
      price: "Site-based subscription plans"
    },
    {
      icon: <FaCloud />,
      title: "Device & Data Cloud",
      description: "Cloud layer that manages connected devices, stores historical data and exposes APIs so your automation data can talk to ERP, MES or reporting tools.",
      features: [
        "Device and gateway management",
        "Long-term data storage",
        "Integration with third-party systems",
        "Scalable infrastructure as you grow"
      ],
      exampleProject: "Cloud platform to collect data from multiple plants and provide a consolidated production dashboard to management.",
      price: "Sized to your fleet and data"
    }
  ];

  const sampleProjects = [
    {
      icon: <FaMicrochip />,
      title: "Automated PCB Test Line",
      client: "Electronics Manufacturer",
      description: "Implemented ICT/FCT test jigs and an automated test station for a motor controller PCB.",
      result: "60% reduction in manual test time and a sharp drop in field failures."
    },
    {
      icon: <FaRobot />,
      title: "Robotic Welding Cell",
      client: "Automotive Supplier",
      description: "Deployed a robotic welding cell with custom fixtures and vision-based position correction.",
      result: "40% higher throughput and consistent weld quality."
    },
    {
      icon: <FaChartBar />,
      title: "Production Monitoring & OEE",
      client: "Fabrication Plant",
      description: "Installed an industrial monitoring and OEE analytics suite across three lines.",
      result: "Identified top downtime reasons and improved overall line efficiency."
    }
  ];

  return (
    <div className="projects-page">
      <SEO
        title="Automation Projects - PCB Test Jigs, Robotics & IoT Solutions"
        description="Explore Robolog Automation's project portfolio including PCB test jig platforms (ICT/FCT), robotic automation cells, industrial IoT monitoring systems, and smart manufacturing solutions. Real-world automation projects with proven results."
        keywords="automation projects, PCB test jig projects, robotic automation projects, industrial IoT projects, ICT FCT test fixtures, robotic welding cells, OEE monitoring systems, manufacturing automation case studies, robolog projects, automation portfolio"
        ogImage="/jig.png"
      />

      {/* Image Gallery Modal */}
      {activeGallery !== null && (
        <div className="gallery-modal" onClick={closeGallery}>
          <div className="gallery-content" onClick={(e) => e.stopPropagation()}>
            <button className="gallery-close" onClick={closeGallery}>
              <FaTimes />
            </button>
            <button className="gallery-nav gallery-prev" onClick={prevImage}>
              <FaArrowLeft />
            </button>
            <div className="gallery-image-container">
              <img
                src={featuredProducts[activeGallery].images[currentImageIndex]}
                alt={`${featuredProducts[activeGallery].title} - ${currentImageIndex + 1}`}
                onError={(e) => {
                  e.target.src = '/placeholder-project.jpg';
                }}
              />
              <div className="gallery-caption">
                {featuredProducts[activeGallery].title} - Image {currentImageIndex + 1} of {featuredProducts[activeGallery].images.length}
              </div>
            </div>
            <button className="gallery-nav gallery-next" onClick={nextImage}>
              <FaArrowRight />
            </button>
            <div className="gallery-thumbnails">
              {featuredProducts[activeGallery].images.map((img, idx) => (
                <div
                  key={idx}
                  className={`gallery-thumb ${idx === currentImageIndex ? 'active' : ''}`}
                  onClick={() => setCurrentImageIndex(idx)}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${idx + 1}`}
                    onError={(e) => {
                      e.target.src = '/placeholder-project.jpg';
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="projects-hero">
        <div className="container">
          <div className="projects-hero-content fade-up">
            <h1>Our Solutions & Project Platforms</h1>
            <p>
              Standardized project platforms that combine hardware, robotics and software—so you
              can plug into automation faster, with less risk and more control.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Products - Expanded */}
      <section className="featured-projects">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">Featured Project Platforms</h2>
            <p className="section-subtitle">
              Our flagship platforms that deliver exceptional performance and reliability.
            </p>
          </div>

          <div className="projects-grid-expanded">
            {featuredProducts.map((product, index) => (
              <div key={index} className={`project-card-expanded ${index % 2 === 0 ? 'slide-in-blur-left' : 'slide-in-blur-right'}`}>
                <div className="project-image-section" onClick={() => openGallery(index)}>
                  <img
                    src={product.mainImage}
                    alt={product.altText}
                    className="project-main-image"
                  />
                  <div className="project-badge">{product.badge}</div>
                  <div className="view-gallery-hint">
                    <span>Click to view gallery</span>
                    <FaArrowRight />
                  </div>
                </div>
                <div className="project-content-expanded">
                  <h3>{product.title}</h3>
                  <p className="seo-tagline">{product.seoTagline}</p>
                  <p className="project-description">{product.description}</p>

                  <div className="project-features-list">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="feature-item">
                        <FaCheckCircle />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="ideal-for">
                    <FaLightbulb />
                    <div>
                      <strong>Ideal For:</strong>
                      <span>{product.idealFor}</span>
                    </div>
                  </div>

                  <div className="example-project">
                    <FaIndustry />
                    <div>
                      <strong>Example Project:</strong>
                      <span>{product.exampleProject}</span>
                    </div>
                  </div>

                  <div className="project-price">{product.price}</div>

                  <div className="project-actions">
                    <Link to="/contact" className="btn btn-primary">
                      {product.primaryBtn}
                      <FaArrowRight />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Automation Hardware & Field Devices */}
      <section className="project-category">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">Automation Hardware & Field Devices</h2>
            <p className="section-subtitle">
              Control panels and field hardware that form the backbone of your automation systems.
            </p>
          </div>

          <div className="category-grid-expanded">
            {hardwareDevices.map((device, index) => (
              <div
                key={index}
                className={`category-item-expanded fade-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="category-icon">
                  {device.icon}
                </div>
                <h3>{device.title}</h3>
                <p className="category-description">{device.description}</p>
                <div className="example-project-small">
                  <strong>Example:</strong> {device.exampleProject}
                </div>
                <div className="category-price">{device.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testing & Validation Systems */}
      <section className="project-category testing-equipment">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">Testing & Validation Systems</h2>
            <p className="section-subtitle">
              PCB test jigs and end-of-line test systems that make quality measurable, not guesswork.
            </p>
          </div>

          <div className="category-grid-expanded">
            {testingEquipment.map((equipment, index) => (
              <div
                key={index}
                className={`category-item-expanded fade-up`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="category-icon">
                  {equipment.icon}
                </div>
                <h3>{equipment.title}</h3>
                <p className="category-description">{equipment.description}</p>
                <div className="example-project-small">
                  <strong>Example:</strong> {equipment.exampleProject}
                </div>
                <div className="category-price">{equipment.price}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Software Suites */}
      <section className="project-category software-projects">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">Software Suites</h2>
            <p className="section-subtitle">
              Software that connects your machines, people and data into one clear picture.
            </p>
          </div>

          <div className="software-grid-expanded">
            {softwareSuites.map((software, index) => (
              <div key={index} className={`software-card-expanded ${index === 0 ? 'slide-in-blur-left' : index === 2 ? 'slide-in-blur-right' : 'fade-up'}`}>
                <div className="software-icon">
                  {software.icon}
                </div>
                <h3>{software.title}</h3>
                <p className="software-description">{software.description}</p>
                <ul className="software-features">
                  {software.features.map((feature, idx) => (
                    <li key={idx}>{feature}</li>
                  ))}
                </ul>
                <div className="example-project-small">
                  <strong>Example:</strong> {software.exampleProject}
                </div>
                <div className="software-pricing">
                  <span className="price">{software.price}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Projects & Use Cases */}
      <section className="sample-projects">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">Sample Projects & Use Cases</h2>
            <p className="section-subtitle">
              A few examples of how customers are using our platforms in real factories.
            </p>
          </div>

          <div className="projects-grid">
            {sampleProjects.map((project, index) => (
              <div key={index} className={`project-card ${index === 0 ? 'slide-in-blur-left' : index === 2 ? 'slide-in-blur-right' : 'fade-up'}`}>
                <div className="project-icon">
                  {project.icon}
                </div>
                <div className="project-header">
                  <h3>{project.title}</h3>
                  <span className="project-client">{project.client}</span>
                </div>
                <p className="project-description">{project.description}</p>
                <div className="project-result">
                  <FaCheckCircle />
                  <span><strong>Result:</strong> {project.result}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Comparison */}
      <section className="platform-comparison">
        <div className="container">
          <div className="text-center fade-up">
            <h2 className="section-title">Platform Comparison</h2>
            <p className="section-subtitle">
              See which platform best fits where you are in your automation journey.
            </p>
          </div>

          <div className="comparison-table">
            <table>
              <thead>
                <tr>
                  <th>Feature</th>
                  <th>PCB Test Jig Platform</th>
                  <th>Robotic & SPM Cell</th>
                  <th>Monitoring & Analytics Suite</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Ideal For</td>
                  <td>Electronics & PCB manufacturers</td>
                  <td>Assembly, welding & handling processes</td>
                  <td>Plants needing visibility and KPIs</td>
                </tr>
                <tr>
                  <td>Primary Benefit</td>
                  <td>Catch defects before shipment</td>
                  <td>Reduce cycle time and manual work</td>
                  <td>Know what is happening in real time</td>
                </tr>
                <tr>
                  <td>Data Capture</td>
                  <td>Test results and traceability</td>
                  <td>Cycle counts, alarms and statuses</td>
                  <td>Consolidated dashboards and reports</td>
                </tr>
                <tr>
                  <td>Customization</td>
                  <td>High – per PCB and connector</td>
                  <td>High – per process and layout</td>
                  <td>Configurable screens and KPIs</td>
                </tr>
                <tr>
                  <td>Typical Engagement</td>
                  <td>Per product family or platform</td>
                  <td>Per cell, station or line</td>
                  <td>Per plant or multi-plant</td>
                </tr>
                <tr>
                  <td>Commercial Model</td>
                  <td>Project-based</td>
                  <td>Turnkey project</td>
                  <td>License / subscription</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta">
        <div className="container">
          <div className="text-center scale-up">
            <h2>Ready to Turn These Platforms Into Your Advantage?</h2>
            <p>
              Tell us what you're building, and we'll match you with the right mix of jigs, cells,
              hardware and software to get results—not just a catalog.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary">
                Get Custom Quote
                <FaArrowRight />
              </Link>
              <Link to="/contact" className="btn btn-outline">
                Talk About Your Use Case
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;
